<script lang="ts" setup>
import { inject, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import FileSaver from 'file-saver'
import { Notify, Dialog } from 'quasar'
import RenameNodeDialog from './RenameNodeDialog.vue'
import DeleteNodeDialog from './DeleteNodeDialog.vue'
import UnlockNodeDialog from './UnlockNodeDialog.vue'
import NodeLockedDialog from './NodeLockedDialog.vue'
import { useApi } from '@/store/useAppStore'
import UserApi from '@/services/api/core/UserApi'
import { ProjectKey } from '@/models/symbols'

import PdfPreviewDialogWithIframe from '@/components/projects/execution/task_actions/document_generation/dialog/PdfPreviewDialogWithIframe.vue'
import ImagePreviewDialog from './ImagePreviewDialog.vue'
import MoveDocumentToUploadTaskDialog from './MoveDocumentToUploadTaskDialog.vue'
import {
  refreshFiles,
  updateLabel,
  orderParent,
  getNode,
  canMoveTo,
  moveNode,
  refreshFlatTree,
} from './DocumentsCommon'
import { useAuthenticatedUser } from '@/store/useAuthenticatedUser'
import { format } from 'date-fns'
import { scroll, useQuasar } from 'quasar'
const { getScrollTarget, setVerticalScrollPosition } = scroll

interface Props {
  element: any
}
const props = withDefaults(defineProps<Props>(), {})

const userApi: UserApi = useApi()
const route = useRoute()

const $q = useQuasar()

const project = inject(ProjectKey)
const documentTreeNodes = inject('documentTreeNodes')
// console.log("documentTreeNodes", documentTreeNodes);

const ticked = inject('ticked')
//const selectedNodeRef = inject("selectedNodeRef");
const documentTreeSelectedNode = inject('documentTreeSelectedNode')
const expandedNodes = inject('expandedNodes')
const viewType = inject('viewType')
const flatViewNodes = inject('flatViewNodes')
const currentDocumentTreeNodes = inject('currentDocumentTreeNodes')

const fileDropZoneRef = ref(null)

const extractingData = ref(false)


// const fileNb = inject("fileNb")
// console.log("creating file node", props.element, fileNb.value)
// fileNb.value = fileNb.value + 1

onMounted(() => {
  // console.log("filenode - onMounted - props.element", props.element)
  props.element.anchorElement = menuRef.value?.$el
})

watch(() => props.element.extractingData, (data, data2) => {
  console.log("props.element changed", props.element, data, data2)
  extractingData.value = data2
})

// function scrollToElement(target, el) {
//   // const target = getScrollTarget(el)
//   let offset = el.offsetTop

//   while (el != target) {
//     el = el.parentNode
//     if (el.classList.contains("relative-position")) {
//       offset += el.offsetTop;
//     }

//     console.log("offset", offset, el.offsetTop, el)
//   }

//   // offset -= 160

//   console.log("target", target, offset);
//   const duration = 200
//   setVerticalScrollPosition(target, offset, duration)
//   // target.scrollTop = offset
// }

// onMounted(() => {
//   // console.log("filenode - onMounted");
//   // const scrollTarget = getScrollTarget(treeRef.value.$el);

// });

// watch(documentTreeSelectedNode, () => {
//   // console.log("filenode - selected node changed", documentTreeSelectedNode.value, props.element.id);
//   // if (documentTreeSelectedNode.value == props.element.id) {
//   //   console.log("filenode - selected node is me");
//   //   // selectedNodeRef.value = me;

//   //   const scrollTarget = document.querySelector(".q-splitter__before")
//   //   // console.log("scrollTarget", scrollTarget, me.value.$el);
//   //   scrollToElement(scrollTarget, menuRef.value.$el);
//   // }
// });

// if (route.query.documentId && route.query.preview) {
//   console.log("have a document to preview", route.query.documentId);
// }

async function renameNode(node, newName) {
  await userApi.renameDocumentTreeNode(project?.value.id, node.id, newName)
  updateLabel(node)
  orderParent(documentTreeNodes, node)
  refreshFlatTree(documentTreeNodes, flatViewNodes, viewType, currentDocumentTreeNodes)
  // refreshFiles(userApi, project, documentTreeNodes);
}

function showRenameFileDialog(node) {
  const dialog = Dialog.create({
    component: RenameNodeDialog,
    componentProps: {
      node: node,
    },
  })
    .onOk((newName) => {
      node.name = newName
      node.label = newName
      renameNode(node, newName)
      dialog.hide()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

async function deleteNode(node) {
  console.log('deleting node', node)
  // const deletedNode = await userApi.deleteDocumentTreeNode(project?.value.id, node.id);
  userApi.deleteDocumentTreeNode(project?.value.id, node.id) // TODO: reactivate await after fix bug in back which is stuck in delete()
  console.log('deleting node in tree')
  let parentNode = getNode(documentTreeNodes.value, node.parentId)
  if (!parentNode) {
    console.log('parent node not found', documentTreeNodes.value)
    parentNode = documentTreeNodes.value[0]
  }
  console.log('parent node', parentNode)
  parentNode.children = parentNode.children.filter((n) => n.id !== node.id)
  console.log('parent node', parentNode)

  console.log('resetting selected node')
  documentTreeSelectedNode.value = null

  refreshFlatTree(documentTreeNodes, flatViewNodes, viewType, currentDocumentTreeNodes)
  // refreshFiles(userApi, project, documentTreeNodes);
}

async function showDeleteFileModal(node) {
  const dialog = Dialog.create({
    component: DeleteNodeDialog,
    componentProps: {
      node: node,
    },
  })
    .onOk(() => {
      dialog.hide()
      deleteNode(node)
    })
    .onCancel(() => {
      dialog.hide()
    })
}

const authenticatedUser = useAuthenticatedUser()

// https://developer.mozilla.org/fr/docs/Web/API/btoa
// https://developer.mozilla.org/fr/docs/Glossary/Base64

function utf8_to_b64(str) {
  return window.btoa(unescape(encodeURIComponent(str)))
}

function b64_to_utf8(str) {
  return decodeURIComponent(escape(window.atob(str)))
}

/* Décoder un tableau d'octets depuis une chaîne en base64 */

function b64ToUint6(nChr) {
  return nChr > 64 && nChr < 91
    ? nChr - 65
    : nChr > 96 && nChr < 123
      ? nChr - 71
      : nChr > 47 && nChr < 58
        ? nChr + 4
        : nChr === 43
          ? 62
          : nChr === 47
            ? 63
            : 0
}

function base64DecToArr(sBase64, nBlocksSize) {
  // Seulement nécessaire si la chaîne en base64 contient
  // des espaces tel qu'un retour de chariot.
  const sB64Enc = sBase64.replace(/[^A-Za-z0-9+/]/g, '')

  const nInLen = sB64Enc.length
  const nOutLen = nBlocksSize ? Math.ceil(((nInLen * 3 + 1) >> 2) / nBlocksSize) * nBlocksSize : (nInLen * 3 + 1) >> 2
  const taBytes = new Uint8Array(nOutLen)
  let nMod3
  let nMod4
  let nUint24 = 0
  let nOutIdx = 0
  for (let nInIdx = 0; nInIdx < nInLen; nInIdx++) {
    nMod4 = nInIdx & 3
    nUint24 |= b64ToUint6(sB64Enc.charCodeAt(nInIdx)) << (6 * (3 - nMod4))
    if (nMod4 === 3 || nInLen - nInIdx === 1) {
      nMod3 = 0
      while (nMod3 < 3 && nOutIdx < nOutLen) {
        taBytes[nOutIdx] = (nUint24 >>> ((16 >>> nMod3) & 24)) & 255
        nMod3++
        nOutIdx++
      }
      nUint24 = 0
    }
  }
  return taBytes
}

/* Chaîne en base64 encodée vers un tableau */
function uint6ToB64(nUint6) {
  return nUint6 < 26
    ? nUint6 + 65
    : nUint6 < 52
      ? nUint6 + 71
      : nUint6 < 62
        ? nUint6 - 4
        : nUint6 === 62
          ? 43
          : nUint6 === 63
            ? 47
            : 65
}

function base64EncArr(aBytes) {
  let nMod3 = 2
  let sB64Enc = ''
  const nLen = aBytes.length
  let nUint24 = 0
  for (let nIdx = 0; nIdx < nLen; nIdx++) {
    nMod3 = nIdx % 3
    // Décommenter le test qui suit afin de découper la
    // chaîne base64 en plusieurs lignes de 80 caractères
    //if (nIdx > 0 && ((nIdx * 4) / 3) % 76 === 0) {
    //  sB64Enc += "\r\n";
    //}
    nUint24 |= aBytes[nIdx] << ((16 >>> nMod3) & 24)
    if (nMod3 === 2 || aBytes.length - nIdx === 1) {
      sB64Enc += String.fromCodePoint(
        uint6ToB64((nUint24 >>> 18) & 63),
        uint6ToB64((nUint24 >>> 12) & 63),
        uint6ToB64((nUint24 >>> 6) & 63),
        uint6ToB64(nUint24 & 63),
      )
      nUint24 = 0
    }
  }
  return sB64Enc.substring(0, sB64Enc.length - 2 + nMod3) + (nMod3 === 2 ? '' : nMod3 === 1 ? '=' : '==')
}

/* Tableau UTF-8 vers une chaîne JS et vice versa */
function UTF8ArrToStr(aBytes) {
  let sView = ''
  let nPart
  const nLen = aBytes.length
  for (let nIdx = 0; nIdx < nLen; nIdx++) {
    nPart = aBytes[nIdx]
    sView += String.fromCodePoint(
      nPart > 251 && nPart < 254 && nIdx + 5 < nLen /* six octets */
        ? /* (nPart - 252 << 30) n'est pas possible pour ECMAScript donc, on utilise un contournement : */
        (nPart - 252) * 1073741824 +
        ((aBytes[++nIdx] - 128) << 24) +
        ((aBytes[++nIdx] - 128) << 18) +
        ((aBytes[++nIdx] - 128) << 12) +
        ((aBytes[++nIdx] - 128) << 6) +
        aBytes[++nIdx] -
        128
        : nPart > 247 && nPart < 252 && nIdx + 4 < nLen /* cinq octets */
          ? ((nPart - 248) << 24) +
          ((aBytes[++nIdx] - 128) << 18) +
          ((aBytes[++nIdx] - 128) << 12) +
          ((aBytes[++nIdx] - 128) << 6) +
          aBytes[++nIdx] -
          128
          : nPart > 239 && nPart < 248 && nIdx + 3 < nLen /* quatre octets */
            ? ((nPart - 240) << 18) + ((aBytes[++nIdx] - 128) << 12) + ((aBytes[++nIdx] - 128) << 6) + aBytes[++nIdx] - 128
            : nPart > 223 && nPart < 240 && nIdx + 2 < nLen /* trois octets */
              ? ((nPart - 224) << 12) + ((aBytes[++nIdx] - 128) << 6) + aBytes[++nIdx] - 128
              : nPart > 191 && nPart < 224 && nIdx + 1 < nLen /* deux octets */
                ? ((nPart - 192) << 6) + aBytes[++nIdx] - 128
                : /* nPart < 127 ? */ /* un octet */
                nPart,
    )
  }
  return sView
}

function strToUTF8Arr(sDOMStr) {
  let aBytes
  let nChr
  const nStrLen = sDOMStr.length
  let nArrLen = 0

  /* correspondance… */
  for (let nMapIdx = 0; nMapIdx < nStrLen; nMapIdx++) {
    nChr = sDOMStr.codePointAt(nMapIdx)
    if (nChr >= 0x10000) {
      nMapIdx++
    }
    nArrLen += nChr < 0x80 ? 1 : nChr < 0x800 ? 2 : nChr < 0x10000 ? 3 : nChr < 0x200000 ? 4 : nChr < 0x4000000 ? 5 : 6
  }
  aBytes = new Uint8Array(nArrLen)

  /* transposition… */
  let nIdx = 0
  let nChrIdx = 0
  while (nIdx < nArrLen) {
    nChr = sDOMStr.codePointAt(nChrIdx)
    if (nChr < 128) {
      /* un octet */
      aBytes[nIdx++] = nChr
    } else if (nChr < 0x800) {
      /* deux octets */
      aBytes[nIdx++] = 192 + (nChr >>> 6)
      aBytes[nIdx++] = 128 + (nChr & 63)
    } else if (nChr < 0x10000) {
      /* trois octets */
      aBytes[nIdx++] = 224 + (nChr >>> 12)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
    } else if (nChr < 0x200000) {
      /* quatre octets */
      aBytes[nIdx++] = 240 + (nChr >>> 18)
      aBytes[nIdx++] = 128 + ((nChr >>> 12) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
      nChrIdx++
    } else if (nChr < 0x4000000) {
      /* cinq octets */
      aBytes[nIdx++] = 248 + (nChr >>> 24)
      aBytes[nIdx++] = 128 + ((nChr >>> 18) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 12) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
      nChrIdx++
    } /* if (nChr <= 0x7fffffff) */ else {
      /* six octets */
      aBytes[nIdx++] = 252 + (nChr >>> 30)
      aBytes[nIdx++] = 128 + ((nChr >>> 24) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 18) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 12) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
      nChrIdx++
    }
    nChrIdx++
  }
  return aBytes
}

async function checkIfCanEditInEditor(element, editor) {
  const treeNode = await userApi.getDocumentTreeNode(project?.value.id, element.id)
  element.editingUser = treeNode.editingUser

  if (!element.editingUser) {
    // Notify.create({
    //   message: "Ouverture de l'éditeur en cours...",
    //   type: "primary",
    // });

    editFile(element, editor)

    return
  }

  const dialog = Dialog.create({
    component: NodeLockedDialog,
    componentProps: {
      node: element,
    },
  })
    .onOk(() => {
      dialog.hide()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

function editFile(element, editor) {
  let command = ''
  if (editor === 'Word') {
    command = 'launch_word'
  }
  if (editor === 'Excel') {
    command = 'launch_excel'
  }
  if (editor === 'PowerPoint') {
    command = 'launch_powerpoint'
  }

  const baseUrl =
    'https://api.staging.comeonlaw.com/' +
    userApi.api.getCurrentClientBaseUrl() +
    '/projects/' +
    project?.value.id +
    '/document_tree/nodes/' +
    element.id

  const downloadUrl = '/download_file/fake_filename?responseType=blob'
  const uploadUrl = '/update_file'
  const lockUrl = '/lock'
  const unlockUrl = '/unlock'
  const fileInfoUrl = '/file_info'

  const deepLinkData = {
    command: 'launch_word',
    user: {
      id: authenticatedUser.id,
      email: authenticatedUser.email,
      firstName: authenticatedUser.firstName,
      lastName: authenticatedUser.lastName,
      fullName: authenticatedUser.fullname,
      accessToken: authenticatedUser.accessToken,
    },
    file: {
      name: element.name,
      type: 'project_document',
      id: element.id,
      baseUrl: baseUrl,
      download: {
        method: 'GET',
        url: downloadUrl,
      },
      upload: {
        method: 'POST',
        url: uploadUrl,
      },
      lock: {
        method: 'POST',
        url: lockUrl,
      },
      unlock: {
        method: 'POST',
        url: unlockUrl,
      },
      info: {
        method: 'GET',
        url: fileInfoUrl,
      },
    },
  }

  console.log(deepLinkData)

  // const base64Encoded = utf8_to_b64(JSON.stringify(deepLinkData));
  const deepLinkDataUTF8 = strToUTF8Arr(JSON.stringify(deepLinkData))
  const base64Encoded = base64EncArr(deepLinkDataUTF8)

  const deepLinkUrl = 'office-manager://files/open?data=' + encodeURIComponent(base64Encoded)

  console.log(deepLinkUrl)
  console.log(deepLinkUrl.length)

  window.location = deepLinkUrl
}

async function downloadFile(node) {
  try {
    const doc = await userApi.downloadDocumentTreeFile(project?.value.id, node.id)

    var reader = new FileReader()
    reader.readAsArrayBuffer(new Blob([doc]))
    reader.onload = function () {
      const newFile = new File([new Blob([reader.result])], 'file', {
        type: 'application/octet-stream',
      })

      FileSaver(newFile, node.name)
    }
    reader.onerror = function (error) {
      // setloadingSaveDoc("error");
      // setTimeout(() => {
      //   setloadingSaveDoc(false);
      // }, 1500);
    }
  } catch (e) {
    console.error(e)
    Notify.create({
      message: 'Erreur lors du téléchargement du document',
      type: 'negative',
    })
  }
}

async function showPdfPreview(node) {
  const BASE_URL = import.meta.env.VITE_API_BASE_URL
  let filenameConverted = encodeURIComponent(node.name)

  let fileUrl =
    BASE_URL +
    userApi.getCurrentClientBaseUrl() +
    `/projects/${project.value.id}/document_tree/nodes/${node.id}/download_pdf_preview/${filenameConverted}?type=preview`
  const dialog = Dialog.create({
    component: PdfPreviewDialogWithIframe,
    componentProps: {
      documentName: node.name,
      fileUrl: fileUrl,
    },
  })
    .onOk(async (data) => {
      dialog.hide()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

async function showMsgPdfPreview(node) {
  const BASE_URL = import.meta.env.VITE_API_BASE_URL
  let filenameConverted = encodeURIComponent(node.name)

  let fileUrl =
    BASE_URL +
    userApi.getCurrentClientBaseUrl() +
    `/projects/${project.value.id}/document_tree/nodes/${node.id}/download_msg_preview/${filenameConverted}?type=preview`
  const dialog = Dialog.create({
    component: PdfPreviewDialogWithIframe,
    componentProps: {
      documentName: node.name,
      fileUrl: fileUrl,
    },
  })
    .onOk(async (data) => {
      dialog.hide()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

const menuRef = ref(null)
const contextualMenuRef = ref(null)

function showPdfPreviewInNewTab(node) {
  menuRef.value.hide()
  contextualMenuRef.value.hide()

  const BASE_URL = import.meta.env.VITE_API_BASE_URL
  let filenameConverted = encodeURIComponent(node.name)

  let fileUrl =
    BASE_URL +
    userApi.getCurrentClientBaseUrl() +
    `/projects/${project.value.id}/document_tree/nodes/${node.id}/download_pdf_preview/${filenameConverted}?type=preview`
  window.open(fileUrl, '_blank').focus()
}

function showMsgPdfPreviewInNewTab(node) {
  menuRef.value.hide()
  contextualMenuRef.value.hide()

  const BASE_URL = import.meta.env.VITE_API_BASE_URL
  let filenameConverted = encodeURIComponent(node.name)

  let fileUrl =
    BASE_URL +
    userApi.getCurrentClientBaseUrl() +
    `/projects/${project.value.id}/document_tree/nodes/${node.id}/download_msg_preview/${filenameConverted}?type=preview`
  window.open(fileUrl, '_blank').focus()
}

function showImagePreviewInNewTab(node) {
  menuRef.value.hide()
  contextualMenuRef.value.hide()

  const BASE_URL = import.meta.env.VITE_API_BASE_URL
  let filenameConverted = encodeURIComponent(node.name)

  let fileUrl =
    BASE_URL +
    userApi.getCurrentClientBaseUrl() +
    `/projects/${project.value.id}/document_tree/nodes/${node.id}/download_image_preview/${filenameConverted}?type=preview`
  window.open(fileUrl, '_blank').focus()
}

function showMoveFileToTaskDialog() {
  const dialog = Dialog.create({
    component: MoveDocumentToUploadTaskDialog,
    componentProps: {
      project: project,
    },
  })
    .onOk(async (destinationTask) => {
      dialog.hide()
      await userApi.moveDocumentTreeDocumentToUploadTask(
        project?.value.id,
        props.element.id,
        destinationTask.value.value.id,
      )

      Notify.create({
        message: 'Document déplacé',
        type: 'primary',
      })

      // refreshFiles(userApi, project, documentTreeNodes);
      window.history.go()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

function showPermissionsDialog() {
}

function isPreviewableFile(file) {
  if (
    file.name.toLowerCase().endsWith('.pdf') ||
    file.name.toLowerCase().endsWith('.docx') ||
    file.name.toLowerCase().endsWith('.doc')
  ) {
    return true
  } else {
    return false
  }
}

function isMsgPreviewableFile(file) {
  if (file.name.toLowerCase().endsWith('.msg')) {
    return true
  } else {
    return false
  }
}

function isImagePreviewableFile(file) {
  if (
    file.name.toLowerCase().endsWith('.jpg') ||
    file.name.toLowerCase().endsWith('.jpeg') ||
    file.name.toLowerCase().endsWith('.png') ||
    file.name.toLowerCase().endsWith('.gif')
  ) {
    return true
  } else {
    return false
  }
}

function showImagePreview(node) {
  const BASE_URL = import.meta.env.VITE_API_BASE_URL
  let filenameConverted = encodeURIComponent(node.name)

  let fileUrl =
    BASE_URL +
    userApi.getCurrentClientBaseUrl() +
    `/projects/${project.value.id}/document_tree/nodes/${node.id}/download_image_preview/${filenameConverted}?type=preview`
  const dialog = Dialog.create({
    component: ImagePreviewDialog,
    componentProps: {
      documentName: node.name,
      fileUrl: fileUrl,
      project: project,
      node: node,
    },
  })
    .onOk(async (data) => {
      dialog.hide()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

function startDrag(evt, item) {
  evt.dataTransfer.dropEffect = 'move'
  evt.dataTransfer.effectAllowed = 'move'
  evt.dataTransfer.setData('itemId', item.id)
  console.log('dragging', item.id, ticked.value)
  if (ticked.value.includes(item.id)) {
    console.log('drag multiple files')
  }
}

async function onDrop(evt) {
  fileDropZoneRef.value.classList.remove('file-drop-zone-hover')

  console.log('viewType', viewType)
  if (viewType.value.value == 'flat') {
    console.log('drop in flat view is not authorized')
    Notify.create({
      message: "Le glisser-déposer de documents n'est pas autorisé dans la vue à plat",
      type: 'negative',
    })
    return
  }

  const itemId = evt.dataTransfer.getData('itemId')
  const item = getNode(documentTreeNodes.value, itemId)

  console.log('dropping', itemId, ticked.value)

  if (!canMoveTo(item, props.element)) {
    return
  }

  console.log('can move')

  if (ticked.value.includes(itemId)) {
    console.log('dropping multiple files')
    await userApi.moveDocumentTreeNodes(project?.value.id, ticked.value, props.element.parentId)
    await refreshFiles(userApi, project, documentTreeNodes)
  } else {
    console.log('dropping single file')
    // await userApi.moveDocumentTreeNode(
    //   project?.value.id,
    //   itemId,
    //   props.element.parentId
    // );
    await moveNode(project, userApi, documentTreeNodes, item, props.element.parentId, expandedNodes)
  }
  refreshFlatTree(documentTreeNodes, flatViewNodes, viewType, currentDocumentTreeNodes)

  ticked.value = []
}

function isWordFile(file) {
  if (file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
    return true
  } else {
    return false
  }
}

function isExcelFile(file) {
  if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
    return true
  } else {
    return false
  }
}

function isPowerPointFile(file) {
  if (file.name.endsWith('.pptx') || file.name.endsWith('.ppt')) {
    return true
  } else {
    return false
  }
}

function isPdfFile(file) {
  if (file.name.endsWith('.pdf')) {
    return true
  } else {
    return false
  }
}

async function showUnlockDialog(node) {
  const dialog = Dialog.create({
    component: UnlockNodeDialog,
    componentProps: {
      node: node,
    },
  })
    .onOk(async (newName) => {
      await userApi.unlockDocumentTreeNode(project?.value.id, node.id)
      node.editingUser = null
      // refreshFiles(userApi, project, documentTreeNodes);
      dialog.hide()
    })
    .onCancel(() => {
      dialog.hide()
    })
}

function hasVersions(element) {
  return element.file.versions && element.file.versions.length > 0
}

function hasUser(element) {
  if (element.id == 'dce27b33-d431-42f2-955d-f3e5dfce5bf2') {
    console.log('FileNode', element)
  }
  if (element.file.user || hasVersions(element)) {
    return true
  } else {
    return false
  }
}

function getUser(element) {
  if (hasVersions(element)) {
    return element.file.versions[0].user
  } else if (element.file.user) {
    return element.file.user
  }
  return ''
}

function getDate(element) {
  if (hasVersions(element)) {
    return element.file.versions[0].createdAt
  } else {
    return element.file.createdAt
  }
}

function formatDate(date) {
  return format(new Date(date), 'dd/MM/yyyy - HH:mm:ss')
}

function onDoublelick(event) {
  console.log('double click', event)
  if (isPreviewableFile(props.element)) {
    showPdfPreview(props.element)
  } else if (isImagePreviewableFile(props.element)) {
    showImagePreview(props.element)
  } else if (isMsgPreviewableFile(props.element)) {
    showMsgPdfPreview(props.element)
  }
}

function onAuxClick(event) {
  console.log('aux click', event)
  if (event.button == 1) {
    console.log('middle click')
    if (isPreviewableFile(props.element)) {
      showPdfPreviewInNewTab(props.element)
    } else if (isImagePreviewableFile(props.element)) {
      showImagePreviewInNewTab(props.element)
    } else if (isMsgPreviewableFile(props.element)) {
      showMsgPdfPreviewInNewTab(props.element)
    }
  }

  if (event.button == 2) {
    console.log('right click')
    onContextMenu(event)
  }
}

const previewInNewTabColor = ref('color: #4068c8')

function onMenuClick(event) {
  console.log('on menu click', event)
  event.preventDefault()
  documentTreeSelectedNode.value = props.element.id
}

function onContextMenu(event) {
  console.log('context menu', documentTreeSelectedNode.value, props.element)
  event.preventDefault()
  documentTreeSelectedNode.value = props.element.id
  contextualMenuRef.value.show(event)
}

function onContextMenuBeforeShow() {
  console.log('context menu before show', documentTreeSelectedNode.value, props.element)
}

function onContextMenuShow() {
  console.log('context menu show', documentTreeSelectedNode.value, props.element)
}

function onContextMenuHide() {
  console.log('context menu hide', documentTreeSelectedNode.value, props.element)
}

function onDragEnter(event) {
  fileDropZoneRef.value.classList.add('file-drop-zone-hover')

  // Needed for onDrop to be fired
  event.preventDefault()
}

function onDragLeave(event) {
  fileDropZoneRef.value.classList.remove('file-drop-zone-hover')
}

async function duplicate(node) {
  console.log('duplicate', node)
  $q.loading.show({
    message: 'Veuillez patienter pendant la duplication...',
    boxClass: 'bg-grey-2 text-grey-9',
    spinnerColor: 'primary',
    delay: 200, // ms
  })
  await userApi.duplicateDocumentTreeNodes(project?.value.id, [node.id])
  await refreshFiles(userApi, project, documentTreeNodes)
  refreshFlatTree(documentTreeNodes, flatViewNodes, viewType, currentDocumentTreeNodes)
  $q.loading.hide()
  Notify.create({
    message: `Duplication réalisée avec succès`,
    type: 'primary',
    timeout: 2000,
  })
}

async function extract(node, model) {
  console.log('extract', node)
  Notify.create({
    message: `Le traitement est lancé. Les informations apparaitront dans quelques dizaines de secondes.`,
    type: 'primary',
    timeout: 2000,
    position: 'center',
  })
  await userApi.fileExtract(project?.value.id, [node.id], model)
  await refreshFiles(userApi, project, documentTreeNodes)
  // Update a value that is watched in ProjectExecution
  // currentDocument.value = getNode(documentTreeNodes.value, documentTreeSelectedNode.value)

}
</script>

<template>
  <!-- 'tree-node':
      !element.editingUser, -->
  <div class="row no-wrap justify-start content-start full-width items-center drag-el drop-zone tree-node" :class="{
    'tree-node-locked': element.editingUser,
  }" style="overflow: hidden" draggable="true" @dragstart="startDrag($event, element)" @drop="onDrop($event)"
    @dragover.prevent @dragenter="onDragEnter($event)" @dragleave="onDragLeave($event)" @dblclick="onDoublelick"
    @auxclick="onAuxClick" @contextmenu="onContextMenu" ref="fileDropZoneRef">
    <q-menu touch-position contextmenu ref="contextualMenuRef" @before-show="onContextMenuBeforeShow"
      @show="onContextMenuShow" @hide="onContextMenuHide">
      <q-list dense style="min-width: 100px">
        <q-item v-if="element.editingUser" clickable v-close-popup @click="showUnlockDialog(element)"
          :disable="element.readOnly">
          <q-item-section>
            <q-item-label>Déverrouiller le fichier</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isWordFile(element)" @click="checkIfCanEditInEditor(element, 'Word')"
          :disable="element.readOnly">
          <q-item-section>
            <q-item-label>Editer dans Word</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isExcelFile(element)" @click="checkIfCanEditInEditor(element, 'Excel')"
          :disable="element.readOnly">
          <q-item-section>
            <q-item-label>Editer dans Excel</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isPowerPointFile(element)"
          @click="checkIfCanEditInEditor(element, 'PowerPoint')" :disable="element.readOnly">
          <q-item-section>
            <q-item-label>Editer dans PowerPoint</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isPdfFile(element)" @click="checkIfCanEditInEditor(element, 'Acrobat')"
          :disable="element.readOnly">
          <q-item-section>
            <q-item-label>Editer dans Acrobat</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup @click="downloadFile(element)">
          <q-item-section>
            <q-item-label>Télécharger</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isPreviewableFile(element)" @click="showPdfPreview(element)" class="row">
          <q-item-section>
            <q-item-label>Prévisualiser</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn @click="showPdfPreviewInNewTab(element)" dense flat round icon="open_in_new"
              class="previewInNewTabButton" @click.stop></q-btn>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isImagePreviewableFile(element)" @click="showImagePreview(element)"
          class="row">
          <q-item-section>
            <q-item-label>Prévisualiser</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn @click="showImagePreviewInNewTab(element)" dense flat round icon="open_in_new"
              class="previewInNewTabButton" @click.stop></q-btn>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup v-if="isMsgPreviewableFile(element)" @click="showMsgPdfPreview(element)"
          class="row">
          <q-item-section>
            <q-item-label>Prévisualiser</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn @click="showMsgPdfPreviewInNewTab(element)" dense flat round icon="open_in_new"
              class="previewInNewTabButton" @click.stop></q-btn>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup @click="showRenameFileDialog(element)" :disable="element.readOnly">
          <q-item-section>
            <q-item-label>Renommer</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup @click="extract(element, 'gpt-3.5')">
          <q-item-section>
            <q-item-label>Résumer le document (GPT-3.5)</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup @click="extract(element, 'gpt-4')">
          <q-item-section>
            <q-item-label>Résumer le document (GPT-4)</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup @click="duplicate(element)">
          <q-item-section>
            <q-item-label>Dupliquer</q-item-label>
          </q-item-section>
        </q-item>
        <q-item clickable v-close-popup @click="showMoveFileToTaskDialog(element)">
          <q-item-section>
            <q-item-label>Déplacer dans une tâche</q-item-label>
          </q-item-section>
        </q-item>
        <!-- <q-item clickable v-close-popup @click="showPermissionsDialog(element)">
          <q-item-section>
            <q-item-label>Permissions</q-item-label>
          </q-item-section>
        </q-item> -->
        <q-item clickable v-close-popup @click="showDeleteFileModal(element)" :disable="element.readOnly">
          <q-item-section>
            <q-item-label text-color="negative">Supprimer</q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>

    <div class="col-auto" style="overflow: hidden">
      <q-icon :name="element.icon || 'description'" size="22px" class="q-mr-sm"
        style="margin-top: 0px; color: #434440" />
    </div>
    <div class="col-shrink" style="overflow: hidden">
      <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
        {{ element.label }}
        <div v-if="element.editingUser" class="editing-user"
          style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
          En cours d'édition par {{ element.editingUser.fullName }}
        </div>
        <div v-if="extractingData" class="editing-user"
          style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
          Extraction des données en cours
        </div>

        <q-tooltip>
          {{ element.label }}
        </q-tooltip>
      </div>
    </div>

    <!-- space filler -->
    <div class="col" style="overflow: hidden"></div>

    <div class="col-auto" style="overflow: hidden">
      <span class="row no-wrap items-center" style="text-align: center; line-height: 36px; font-size: 12px">
        <span class="col-auto" style="white-space: nowrap; padding-right: 8px" v-if="hasUser(element)">
          <span>{{ getUser(element).fullName }}</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <span>{{ formatDate(getDate(element)) }}</span>
        </span>
        <span class="col-auto" style="white-space: nowrap; padding-right: 8px"
          v-if="!hasUser(element)"><span>{{}}</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <span v-if="element.file.fileModificationDatetime != undefined">{{
            formatDate(element.file.fileModificationDatetime)
          }}</span>
          <span v-if="element.file.fileModificationDatetime == undefined">{{ formatDate(getDate(element)) }}</span>
        </span>
        <q-btn-dropdown class="col-auto" flat rounded style="padding-left: 8px; padding-right: 8px"
          dropdown-icon="more_vert" clickable @click.stop="onMenuClick" ref="menuRef">
          <q-list dense>
            <q-item v-if="element.editingUser" clickable v-close-popup @click="showUnlockDialog(element)">
              <q-item-section>
                <q-item-label>Déverrouiller le fichier</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isWordFile(element)" @click="checkIfCanEditInEditor(element, 'Word')"
              :disable="element.readOnly">
              <q-item-section>
                <q-item-label>Editer dans Word</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isExcelFile(element)"
              @click="checkIfCanEditInEditor(element, 'Excel')" :disable="element.readOnly">
              <q-item-section>
                <q-item-label>Editer dans Excel</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isPowerPointFile(element)"
              @click="checkIfCanEditInEditor(element, 'PowerPoint')" :disable="element.readOnly">
              <q-item-section>
                <q-item-label>Editer dans PowerPoint</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isPdfFile(element)"
              @click="checkIfCanEditInEditor(element, 'Acrobat')" :disable="element.readOnly">
              <q-item-section>
                <q-item-label>Editer dans Acrobat</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="downloadFile(element)">
              <q-item-section>
                <q-item-label>Télécharger</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isPreviewableFile(element)" @click="showPdfPreview(element)"
              class="row">
              <q-item-section>
                <q-item-label>Prévisualiser</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn @click="showPdfPreviewInNewTab(element)" dense flat round icon="open_in_new"
                  class="previewInNewTabButton" @click.stop></q-btn>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isImagePreviewableFile(element)" @click="showImagePreview(element)"
              class="row">
              <q-item-section>
                <q-item-label>Prévisualiser</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn @click="showImagePreviewInNewTab(element)" dense flat round icon="open_in_new"
                  class="previewInNewTabButton" @click.stop></q-btn>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup v-if="isMsgPreviewableFile(element)" @click="showMsgPdfPreview(element)"
              class="row">
              <q-item-section>
                <q-item-label>Prévisualiser</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn @click="showMsgPdfPreviewInNewTab(element)" dense flat round icon="open_in_new"
                  class="previewInNewTabButton" @click.stop></q-btn>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="showRenameFileDialog(element)" :disable="element.readOnly">
              <q-item-section>
                <q-item-label>Renommer</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="extract(element, 'gpt-3.5')">
              <q-item-section>
                <q-item-label>Résumer le document (GPT-3.5)</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="extract(element, 'gpt-4')">
              <q-item-section>
                <q-item-label>Résumer le document (GPT-4)</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="duplicate(element)">
              <q-item-section>
                <q-item-label>Dupliquer</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="showMoveFileToTaskDialog(element)">
              <q-item-section>
                <q-item-label>Déplacer dans une tâche</q-item-label>
              </q-item-section>
            </q-item>
            <!-- <q-item clickable v-close-popup @click="showPermissionsDialog(element)">
              <q-item-section>
                <q-item-label>Permissions</q-item-label>
              </q-item-section>
            </q-item> -->
            <q-item clickable v-close-popup @click="showDeleteFileModal(element)" :disable="element.readOnly">
              <q-item-section>
                <q-item-label text-color="negative">Supprimer</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </span>
    </div>
  </div>
  <!-- </node-container> -->
</template>

<style lang="scss" scoped>
.file-name {
  font-size: 16px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  // border: 1px solid;
  // text-wrap: nowrap;
}

.file-date {
  color: #2c2c2c;
}

.prevent-select {
  -webkit-user-select: none;
  /* Safari */
  -ms-user-select: none;
  /* IE 10 and IE 11 */
  user-select: none;
  /* Standard syntax */
}

.drop-zone {
  // margin-bottom: 10px;
  // padding: 10px;
}

.drag-el {
  // cursor: grab;
  // margin-bottom: 10px;
  // padding: 5px;
}

.editing-user {
  font-style: italic;
  font-size: 13px;
  color: #431010;
  margin-top: -4px;
}

.previewInNewTabButton {
  color: #cccccc;
}

.previewInNewTabButton:hover {
  color: #777777;
  background-color: #bbbbbb;
}

.file-drop-zone-hover {
  background-color: lightgrey;
}
</style>
